import { ArrowLeft } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * Where the page on screen was opened from, when that is a page of its own: the app shell sets it
 * from the route map (`settingsBackLink` in `lib/nav.ts`) for the pages behind Settings, and
 * `PageHeader` draws it. A screen does not pass it itself, so a page moved behind Settings gets its
 * way back without an edit, and one moved out loses it.
 */
export const PageBackContext = React.createContext<{ to: string; label: string } | null>(null);

/**
 * Every screen opens the same way: title on the left, primary action on the right, optional
 * one-line description. Consistency here is most of what makes a console feel like one product
 * rather than a pile of pages.
 */
export function PageHeader({
  title, description, action, className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  const back = React.useContext(PageBackContext);
  return (
    <>
      {/* The link a record's page carries back to its list (`← Campaigns`), drawn the same way. */}
      {back && (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {back.label}
        </Link>
      )}
      <div className={cn("flex items-start justify-between gap-4 pb-4", className)}>
        <div className="min-w-0 space-y-1">
          <h1 className="text-xl font-semibold tracking-tight text-text">{title}</h1>
          {description && <p className="text-sm text-text-muted">{description}</p>}
        </div>
        {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
      </div>
    </>
  );
}

/** The scroll container for a screen. Max width so tables stay readable on a wide monitor. */
export function Page({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mx-auto w-full max-w-7xl px-6 py-6", className)} {...props} />;
}

/** A filter/search strip above a table. */
export function Toolbar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 border-b border-border px-3 py-2", className)}
      {...props}
    />
  );
}
